import { useCallback, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import Loader from '../../elements/variants/calendar/Loader/Loader'
import { ArtistProps } from '../../types'
import styles from './Calendar.module.scss'
import { disableBodyScroll } from 'utils'
import { useWindowSize } from 'utils/hooks'
import BottomNav from '../../elements/variants/calendar/BottomNav/BottomNav'
import TopNav from '../../elements/variants/calendar/TopNav/TopNav'
import SelectedDay from '../../elements/variants/calendar/SelectedDay/SelectedDay'
import Month from '../../elements/variants/calendar/Month/Month'
import { CalendarProps } from '~types'
import { useEventsStore } from 'store/useEventsStore'
import { FormattedEvent } from '../../types/Event'
import { useCalendar } from '../../hooks/useCalendar'
import { useSearchParams } from 'next/navigation'
import { useCalendarNavigation } from '../../hooks/useCalendarNavigation'
import { DATE_FORMAT } from '~constants'
import { filterPastEvents } from '../../utils'
import { normalizePath } from 'utils'

const CalendarView = ({
  eventDetailsDisclaimer,
  artists,
  groupTicketsOfferText,
  iconNavigationGroup
}: CalendarProps) => {
  const calendarRef = useRef<HTMLDivElement | null>(null)
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')
  const router = useRouter()
  const { asPath } = useRouter()
  const searchParams = useSearchParams()
  const setSelectedEvent = useEventsStore(s => s.setSelectedEvent)
  const setActiveShowId = useEventsStore(s => s.setActiveShowId)
  const HEADER_HEIGHT = 60 // @TODO: will create useNavHeight hook in v6.17

  const { loading, setSelectedDate } = useCalendar({
    initialDate: dayjs(),
    artists,
    isTablet: isBelowMd
  })

  const { activeMonthList, activeShowId, eventsByDate, selectedEvent, selectedMonth } = useEventsStore()
  const { handleNavigation, setSelectedMonth } = useCalendarNavigation(setSelectedDate)
  useEffect(() => {
    const id = searchParams.get('id')
    // Replaces dashes with forward slashes to address Safari parsing bug
    const startDate = searchParams.get('startdate')?.replace(/-/g, '/')

    if (!id || !startDate || !eventsByDate || Object.keys(eventsByDate)?.length === 0) return

    setSelectedDate(dayjs(startDate).toDate())
    const dayKey = dayjs(startDate).format(DATE_FORMAT.SHORT_DATE_FORMAT)
    const filteredEvents = filterPastEvents(eventsByDate[dayKey] || [])
    const matchingEvent = filteredEvents.find(event => event?.id === id)
    const artistDetail = artists.find(artist => artist?.artistId === matchingEvent?.artistId) as ArtistProps
    if (matchingEvent) {
      setSelectedEvent({
        events: filterPastEvents(eventsByDate[dayKey]),
        artistDetail
      })
      setActiveShowId(id)
    }
  }, [eventsByDate, searchParams, artists])

  // Push to URL
  const updateQueryString = useCallback(
    (event: FormattedEvent, blockUpdate: boolean = false) => {
      // Prevents selected event from different selectedMonth from navigating to that event's month
      if (blockUpdate) return

      router.push(
        {
          pathname: normalizePath(asPath),
          query: {
            startdate: dayjs(event.dateTime).format('MM-DD-YYYY'),
            id: event.id
          }
        },
        undefined,
        { shallow: true }
      )
    },
    [router]
  )

  // Determines if selected event is from a different month than the selectedMonth
  const isDifferentMonth = (eventMonthKey: string, selectedMonthValue: string) => {
    const eventMonth = dayjs(eventMonthKey, 'YYYY-MM').format('YYYY-MM')
    const selectedMonth = dayjs(selectedMonthValue, 'MMMM-YYYY').format('YYYY-MM')
    return eventMonth !== selectedMonth
  }

  const handleDateSelection = useCallback(
    (events: FormattedEvent[], artistDetail: ArtistProps) => {
      // filter past events when selecting date is today
      const filteredEvents = filterPastEvents(events)
      const event = filteredEvents.find(e => e.artistId === artistDetail.artistId) as FormattedEvent
      setSelectedEvent({ events: filteredEvents, artistDetail })
      if (event) {
        setActiveShowId(event.id)
        const differentMonth = isDifferentMonth(event.monthKey, selectedMonth.value)
        updateQueryString(event, differentMonth)
      }
    },
    [setSelectedEvent, setActiveShowId, selectedMonth.value, updateQueryString]
  )

  const handleTimeSelection = useCallback(
    (id: string) => {
      if (!selectedEvent) return
      const event = selectedEvent.events.find(e => e.id === id)
      if (event) {
        setActiveShowId(event.id)
        const differentMonth = isDifferentMonth(event.monthKey, selectedMonth.value)
        updateQueryString(event, differentMonth)
      }
    },
    [selectedEvent, selectedMonth.value, updateQueryString]
  )

  useEffect(() => {
    disableBodyScroll(Boolean(selectedEvent))
  }, [selectedEvent])

  const closeSelectedSelectedDay = useCallback(() => {
    setSelectedEvent(null)
    setActiveShowId(null)
    router.push(normalizePath(asPath), undefined, { shallow: true })
  }, [setSelectedEvent, setActiveShowId, router])

  const handleNavigationWithScroll = (key: string, shouldScrollTop: boolean = false) => {
    // Scroll the Calendar into view with NavigationHeader offset
    if (shouldScrollTop && calendarRef.current) {
      // -16px to separate from header CTA button
      const top = calendarRef.current.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT - 16
      // setTimeout hack to allow time for loading
      setTimeout(() => {
        window.scrollTo({
          top,
          behavior: 'smooth'
        })
      }, 500)
    }
    handleNavigation(key)
  }

  return (
    <>
      <div className={styles['calendar']} ref={calendarRef}>
        <TopNav
          setSelectedMonth={setSelectedMonth}
          handleNavigation={handleNavigation}
          isTablet={isBelowMd}
          iconNavigationGroup={iconNavigationGroup}
          isLoading={loading}
        />
        {loading ? (
          <Loader />
        ) : (
          <>
            <Month
              month={dayjs(selectedMonth?.value, 'MMMM-YYYY')}
              variant={isBelowMd ? 'tablet' : 'desktop'}
              handleDateSelection={handleDateSelection}
            />
            <BottomNav
              month={selectedMonth?.value ? dayjs(selectedMonth?.value, 'MMMM-YYYY') : dayjs()}
              handleNavigation={handleNavigationWithScroll}
              activeMonthList={activeMonthList}
            />
          </>
        )}
      </div>

      <SelectedDay
        isOpen={Boolean(selectedEvent)}
        disclaimer={eventDetailsDisclaimer ?? { html: '' }}
        eventInfo={selectedEvent}
        activeShowId={activeShowId}
        onClose={closeSelectedSelectedDay}
        onTimeSelect={handleTimeSelection}
        groupTicketsOfferText={groupTicketsOfferText ?? { html: '' }}
      />
    </>
  )
}

export default CalendarView
